import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { savedFiltersOptions } from './savedFilters.options';

const CHANNEL_NAME = 'gx_saved_filters_sync';

/**
 * Notifies other browser tabs that a saved-filters mutation has occurred.
 * Opens a short-lived BroadcastChannel, posts the message, then closes it.
 * Other tabs refresh from the server when they receive the signal.
 */
export const broadcastSavedFiltersMutation = (): void => {
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage({ type: 'saved-filters-cache-invalidated' });
  channel.close();
};

/**
 * Listens for saved-filters mutations broadcast from other tabs and refetches
 * the saved-filters query so this tab stays in sync automatically.
 *
 * Call once in AppLayout (or any persistent authenticated component).
 */
export const useSavedFiltersSync = (): void => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);

    const refetchSavedFiltersCache = () => {
      queryClient
        .refetchQueries({ queryKey: savedFiltersOptions.all(), type: 'active' })
        .catch(() => undefined);
    };

    channel.onmessage = refetchSavedFiltersCache;

    return () => {
      channel.close();
    };
  }, [queryClient]);
};
